import type { OfferKind } from '@revelio/core/global-ai-assistant/offer-marker';
import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import type { ClientToolDeps } from './global-ai-client-tools';
import type { ToolEmit } from './global-ai-tool-progress';
import {
  buildFilterResolutionDigest,
  buildScreenerFilterResolutionDigest,
  type ChartDataDigest,
} from './turn-outcome-digest';

/**
 * Discriminated outcome of a loop-initiated navigate. Success carries the
 * landed page's display name; failure carries a machine-readable `reason` so
 * the client tool can narrate an HONEST result to the model instead of
 * reporting a phantom navigation. `no_access` additionally carries the
 * entitlement `featureLabel`/`message` from the route-access gate.
 */
export type NavigateToOptionResult =
  | { navigatedTo: string }
  | { notNavigated: true; reason: 'unknown_page' }
  | { notNavigated: true; reason: 'cancelled' }
  | {
      notNavigated: true;
      reason: 'no_access';
      featureLabel: string;
      message: string;
    };

export type ClientToolHandles = {
  /**
   * Drive the guarded router for a `__std__:` option id. Used by
   * `apply_filters`' internal post-filter discovery navigation and by the
   * nav-confirm chip's click handler — NOT by the `navigate` tool, which is
   * terminal and only requests a confirmation (see `requestNavigateConfirm`).
   */
  navigateToOption: (optionId: string) => Promise<NavigateToOptionResult>;
  /**
   * Terminal handler for the `navigate` tool: resolve the optionId to a page
   * name and append a confirmation chip to the transcript WITHOUT navigating.
   * Returns `{ unknownPage: true }` when the id doesn't map to a real page.
   */
  requestNavigateConfirm: (
    optionId: string,
    /**
     * When false, the nav-confirm chip navigates on click WITHOUT resuming the
     * turn (no continuationPrompt). Used by `offer_page_or_custom`'s page chip,
     * whose page-open is terminal. Defaults to true (normal `navigate`).
     */
    withContinuation?: boolean,
    /**
     * Data-grounded offer: the resolved search_filters artifact the offer's
     * figures came from. Stored on the nav_confirm message so the click
     * applies it on the destination (deterministic text=link).
     */
    grounded?: {
      resolvedFilters: TalentDiscoveryAiFilterSearchResponseV2;
      entityType?: GlobalAssistantEntityType;
    }
  ) => { pageName: string; messageId: string } | { unknownPage: true };
  applyFilters: (
    filterResponse: unknown,
    target: string,
    merge?: boolean
  ) => Promise<{
    response:
      | TalentDiscoveryAiFilterSearchResponseV2
      | CompanyDiscoveryAiOutput;
    targetPage: string;
  }>;
  renderChartAndAwaitDigest: (payload: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
    standardPagePath?: string;
  }) => Promise<ChartDataDigest>;
  /**
   * Headless single-value digest fetch (DP3): resolves the settled digest via
   * the offscreen digest host — no chart UI, no transcript message, no drawer
   * involvement. The direct-answer path (`get_data_answer`, `render_chart`'s
   * answerAsText branch) uses this instead of `renderChartAndAwaitDigest`.
   */
  fetchChartDigest: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
  }) => Promise<ChartDataDigest>;
  /**
   * Append the card-less `single_chart` offer:page context message for a
   * SUCCESSFUL single-value answer (standardPagePath + filters + prompt — the
   * drawer's inline-link click reads them). Metadata only: answerAsText
   * messages are excluded from chart-preview liveness and never mount.
   */
  appendAnswerContextChart: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    standardPagePath: string;
  }) => void;
  /**
   * `create_custom_page` handle. Two modes, selected by the drawer's
   * confirmed-build ref:
   *  - OFFER (default): append a "Build the dashboard" chip and end the turn —
   *    resolves `{ offered: true }`.
   *  - BUILD-HERE (ref set by the chip click): run the CREATE page-gen on the
   *    CURRENT empty draft (no nav), collect the page's widget digests, and
   *    resolve `{ built: true, ok, digests }` so the client tool narrates a
   *    grounded CREATE summary.
   */
  requestCreateCustomPage: (
    prompt: string,
    entityType: string,
    emit?: ToolEmit,
    // Force OFFER mode (never BUILD-HERE) and clear any stale pending-build ref.
    // Used by offer_page_or_custom, a pure offer that must never build.
    forceOffer?: boolean,
    // Data-grounded offer artifact (offer_page_or_custom's filterArtifactId):
    // stored on the build chip as the lazy build's landing-filter fallback.
    groundedFilters?: TalentDiscoveryAiFilterSearchResponseV2
  ) => Promise<
    | { offered: true; messageId: string }
    | {
        built: true;
        ok: boolean;
        digests: ChartDataDigest[];
        expectedCount?: number;
      }
  >;
  /**
   * Emit an `update_current_page` command and resolve with whether it confirmed
   * in time (`ok`) plus the page's collected widget digests, so the client tool
   * narrates an honest, GROUNDED result (the actual widgets now on the page)
   * instead of a generic "current page updated" (FE-2117). `digests` is empty
   * on timeout or when the collector hasn't settled.
   */
  updateCurrentPage: (
    prompt: string,
    emit?: ToolEmit
  ) => Promise<{
    ok: boolean;
    digests: ChartDataDigest[];
    expectedCount?: number;
  }>;
  /**
   * Append report-offer chips to the transcript for the given report types
   * (the user clicks a chip to actually launch a report). Resolves each
   * report's display name so the model can name them in its narration;
   * unresolvable report types are skipped. `messageId` is the id of the
   * appended `report_offers` message — the `offer:report` stamp source the
   * handler passes to `appendAssistantText` so the narration's inline
   * trigger(s) wire to (and suppress) those cards.
   */
  offerReport: (
    reportTypes: string[],
    prompt: string
  ) => Promise<{ reportNames: string[]; messageId?: string }>;
  askUser: (question: string, options?: string[]) => void;
  /**
   * Append an assistant `text` transcript message carrying the given narration.
   * The offer tools use this to render their marker-bearing `narration` param as
   * the turn's assistant text, so the drawer turns the `[…](offer:nav|build)`
   * markers into inline trigger spans (and hides the redundant chip). Reuses the
   * runner's `appendTranscript`.
   */
  appendAssistantText: (
    text: string,
    offerTargets?: Partial<Record<OfferKind, string>>
  ) => void;
  /**
   * Id of this turn's latest single-value answer chart that has a matching
   * standard page — the `offer:page` stamp source. Only such charts qualify,
   * so an ordinary chart rendered later in the turn can't hijack the target.
   */
  getLastStandardPageAnswerChartId: () => string | undefined;
};

/**
 * Narrow the widened apply response to the TD v2 shape the digest builder
 * understands. The screener `CompanyDiscoveryAiOutput` carries `filters` +
 * `unknownFilters` but no root `relevantPromptText`.
 */
function isTalentDiscoveryFilterResponse(
  value: TalentDiscoveryAiFilterSearchResponseV2 | CompanyDiscoveryAiOutput
): value is TalentDiscoveryAiFilterSearchResponseV2 {
  return (
    typeof value === 'object' && value !== null && 'relevantPromptText' in value
  );
}

export function createClientToolDeps(
  handles: ClientToolHandles
): ClientToolDeps {
  return {
    requestNavigateConfirm: (input) =>
      handles.requestNavigateConfirm(
        input.optionId,
        input.withContinuation,
        // The deps layer is stringly-typed at the tool boundary; the artifact
        // was produced by search_filters, so the cast re-asserts its real type.
        input.resolvedFilters !== undefined
          ? {
              resolvedFilters:
                input.resolvedFilters as TalentDiscoveryAiFilterSearchResponseV2,
              // No fallback here: an absent anchor (grounded `navigate`, which
              // has no entityType input) stays absent so the click path infers
              // it from the destination path instead of pinning 'company'.
              entityType: input.entityType as
                | GlobalAssistantEntityType
                | undefined,
            }
          : undefined
      ),
    applyFilters: async (input) => {
      const { response, targetPage } = await handles.applyFilters(
        input.filterResponse,
        input.target,
        input.merge
      );
      // The TD digest builder reads the TD v2 shape (`relevantPromptText`
      // etc.). A company-discovery (screener) artifact lacks those root
      // fields, so it has a dedicated builder that reads the screener
      // `primary_selector` / `entity_info_selectors` / `segments` shape.
      if (isTalentDiscoveryFilterResponse(response)) {
        return buildFilterResolutionDigest({ response, targetPage });
      }
      return buildScreenerFilterResolutionDigest({ response, targetPage });
    },
    renderChart: (input) => handles.renderChartAndAwaitDigest(input),
    fetchChartDigest: (input) => handles.fetchChartDigest(input),
    appendAnswerContextChart: (input) =>
      handles.appendAnswerContextChart(input),
    requestCreateCustomPage: (input, emit) =>
      handles.requestCreateCustomPage(
        input.prompt,
        input.entityType,
        emit,
        input.forceOffer,
        // Stringly-typed tool boundary; the artifact was produced by
        // search_filters, so the cast re-asserts its real type (same pattern
        // as requestNavigateConfirm.resolvedFilters above).
        input.groundedFilters as
          | TalentDiscoveryAiFilterSearchResponseV2
          | undefined
      ),
    updateCurrentPage: async (input, emit) => {
      const { ok, digests, expectedCount } = await handles.updateCurrentPage(
        input.prompt,
        emit
      );
      return { ok, digests, expectedCount };
    },
    offerReport: (input) =>
      handles.offerReport(input.reportTypes, input.prompt),
    askUser: (input) => handles.askUser(input.question, input.options),
    appendAssistantText: (text, offerTargets) =>
      handles.appendAssistantText(text, offerTargets),
    getLastStandardPageAnswerChartId: () =>
      handles.getLastStandardPageAnswerChartId(),
  };
}
